import { Avatar, Icon, IconButton, Text, HStack, VStack } from '@genial/components';
import { Config } from '@genial/config';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Pressable } from 'native-base';
import React from 'react';
import { Linking } from 'react-native';
import { useRecoilValue } from 'recoil';

import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import {
  masterStateSelectedAdvisorNameSelector,
  masterStateSelectedAdvisorSelector,
} from '../../../../store/master/selector';
import { sessionState } from '../../../../store/session/atom';
import { ImpersonateMenuAction, ImpersonateMenuComponent, ImpersonateMenuPage } from '../../types';
import { ActionButton } from '../ActionButton';
import { Separator } from '../Separator';
import { SignoutButton } from '../SignoutButton';

export const UserSelection: React.FC<ImpersonateMenuComponent> = ({ onAction, width }) => {
  const { userProfile, isMaster } = useRecoilValue(sessionState);
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const selectedAdvisor = useRecoilValue(masterStateSelectedAdvisorSelector);
  const selectedAdvisorName = useRecoilValue(masterStateSelectedAdvisorNameSelector);
  const isMobile = useBreakpointValue({ base: true, sm: false });

  const handleClose = () => {
    onAction(ImpersonateMenuAction.Close);
  };

  const handleProfile = () => {
    onAction(ImpersonateMenuAction.Close);
    navigate({ name: 'AngularFrame_MyProfile', params: { state: 'app.meuUsuario' } });
  };

  const handleAccessLeves = () => {
    onAction(ImpersonateMenuAction.Close);
    navigate({ name: 'AngularFrame_AccessLevels', params: { state: 'app.niveisAcesso' } });
  };

  const handleTermsAndContracts = () => {
    onAction(ImpersonateMenuAction.Close);
    navigate('DocumentsCenter');
  };

  const handleHelpCenter = async () => {
    onAction(ImpersonateMenuAction.Close);

    const isUrlOpenable = await Linking.canOpenURL(Config.GenialHelpUrl);

    if (isUrlOpenable) {
      await Linking.openURL(Config.GenialHelpUrl);
    }
  };

  const handleImpersonate = () => {
    onAction(ImpersonateMenuAction.Transition, ImpersonateMenuPage.Impersonate);
  };

  return (
    <VStack
      space={Spacing['stack.xs']}
      paddingTop={Spacing['inset.sm']}
      paddingX={Spacing['inset.xs']}
      width={width}>
      <VStack space={Spacing['stack.quarck']}>
        <HStack justifyContent="space-between" alignItems="center">
          <Text flex={1} fontWeight={Typography['fontweight.bold']}>
            Perfil do parceiro
          </Text>
          <IconButton
            onPress={handleClose}
            icon="Close"
            iconSize="xxs"
            testId="back"
            iconColor={Color['neutral.black']}
          />
        </HStack>
        <HStack space={Spacing['stack.xxxs']} alignItems="center">
          <Avatar
            initials={userProfile?.initials}
            bgColor="brand.tertiary.principal"
            textColor="neutral.white"
            testId="avatar"
          />
          <VStack space={Spacing['stack.nano']} flex={1}>
            <HStack space={Spacing['stack.quarck']}>
              <Text
                fontWeight="bold"
                fontSize={Typography['fontsize.md']}
                ellipsizeMode="tail"
                numberOfLines={1}>
                {userProfile?.nomeRecurso}
              </Text>
              <Text>({userProfile?.idAssessorIntranet})</Text>
            </HStack>
            <Text color="neutral.medium" ellipsizeMode="tail" numberOfLines={1}>
              {userProfile?.email}
            </Text>
            <Text color="neutral.medium" fontWeight="semibold">
              {userProfile?.nomeParceiro}
            </Text>
          </VStack>
        </HStack>
      </VStack>
      <Separator />
      <ActionButton label="Ver perfil" icon="Client" onPress={handleProfile} />
      {(userProfile?.isGestor || userProfile?.masterParceiro) && (
        <ActionButton label="Níveis de acesso" icon="Permission" onPress={handleAccessLeves} />
      )}
      <ActionButton
        label="Central de Documentos"
        icon="Contracts"
        onPress={handleTermsAndContracts}
      />
      <ActionButton label="Central de ajuda" icon="Info" onPress={handleHelpCenter} />
      <Separator />
      {isMobile && (
        <>
          <Pressable onPress={handleImpersonate} disabled={!isMaster}>
            <HStack space={Spacing['stack.xxxs']} alignItems="center">
              <Icon name="IdCard" size="xs" color={Color['brand.tertiary.principal']} />
              <VStack space={Spacing['stack.nano']} flex={1}>
                <Text fontSize={Typography['fontsize.xs']} color="neutral.medium">
                  Visualizando dados de
                </Text>
                <Text fontSize={Typography['fontsize.md']} numberOfLines={2}>
                  {selectedAdvisor !== undefined ? selectedAdvisorName : userProfile?.nomeRecurso}
                </Text>
              </VStack>
              {isMaster && <Icon name="ChevronRight" size="xs" color={Color['neutral.medium']} />}
            </HStack>
          </Pressable>
          <Separator />
        </>
      )}
      <SignoutButton />
    </VStack>
  );
};

UserSelection.displayName = 'UserSelection';
